import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Ticket, Pencil } from 'lucide-react';
import {
  signupCouponsApi,
  describeSignupCouponBenefit,
  type SignupCoupon,
  type SignupCouponBenefitType,
  type SignupPlanCode,
  type UpsertSignupCouponPayload,
} from '@/api/signupCoupons';
import { Button, Modal, Input, Loading, EmptyState } from '@/components/ui';
import { formatDate } from '@/utils/date';

const PLANS: { value: SignupPlanCode; label: string }[] = [
  { value: 'ignite', label: 'Ignite' },
  { value: 'accelerate', label: 'Accelerate' },
  { value: 'command', label: 'Command' },
  { value: 'business', label: 'Business' },
];

const BENEFITS: { value: SignupCouponBenefitType; label: string }[] = [
  { value: 'free_months', label: 'Free months (no payment)' },
  { value: 'discount', label: 'Discount on first month(s)' },
];

function usesLabel(c: SignupCoupon): string {
  const max = c.max_uses != null ? String(c.max_uses) : '∞';
  return `${c.used_count} / ${max}`;
}

function expiryLabel(c: SignupCoupon): string {
  if (!c.expires_at) return 'Never';
  return formatDate(c.expires_at);
}

export default function CouponsPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SignupCoupon | null>(null);

  const { data: coupons, isLoading } = useQuery({
    queryKey: ['signup-coupons'],
    queryFn: () => signupCouponsApi.list(),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      signupCouponsApi.setActive(id, active),
    onSuccess: () => {
      toast.success('Coupon updated');
      qc.invalidateQueries({ queryKey: ['signup-coupons'] });
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (c: SignupCoupon) => {
    setEditing(c);
    setModalOpen(true);
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Ticket className="h-5 w-5 text-indigo-400" />
          <div>
            <h1 className="text-xl font-semibold text-white">Signup Coupons</h1>
            <p className="text-xs text-ink-muted mt-0.5">
              Invite-only codes for free or discounted public signup trials
            </p>
          </div>
        </div>
        <Button onClick={openCreate}>
          <Plus size={16} className="mr-1" /> New coupon
        </Button>
      </div>

      {isLoading ? (
        <Loading text="Loading signup coupons…" />
      ) : !(coupons ?? []).length ? (
        <EmptyState
          icon={<Ticket className="h-8 w-8" />}
          title="No signup coupons yet"
          description="Create invite codes for the /start signup page"
        />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-muted text-xs uppercase">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Benefit</th>
                <th className="px-4 py-3">Uses</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {coupons!.map((c) => (
                <tr key={c.id} className="border-t border-navy-border hover:bg-white/[0.02]">
                  <td className="px-4 py-3 font-mono text-white">{c.code}</td>
                  <td className="px-4 py-3 capitalize">{c.plan_code}</td>
                  <td className="px-4 py-3">{describeSignupCouponBenefit(c)}</td>
                  <td className="px-4 py-3">{usesLabel(c)}</td>
                  <td className="px-4 py-3">{expiryLabel(c)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleMut.mutate({ id: c.id, active: !c.active })}
                      className={`text-xs font-medium px-2 py-1 rounded-full ${
                        c.active ? 'bg-teal-500/20 text-teal-300' : 'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {c.active ? 'active' : 'inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => openEdit(c)}
                      className="text-indigo-400 hover:text-indigo-300"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CouponModal
        open={modalOpen}
        coupon={editing}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false);
          qc.invalidateQueries({ queryKey: ['signup-coupons'] });
        }}
      />
    </div>
  );
}

function CouponModal({
  open,
  coupon,
  onClose,
  onSaved,
}: {
  open: boolean;
  coupon: SignupCoupon | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!coupon;
  const [code, setCode] = useState('');
  const [planCode, setPlanCode] = useState<SignupPlanCode>('accelerate');
  const [benefitType, setBenefitType] = useState<SignupCouponBenefitType>('free_months');
  const [freeMonths, setFreeMonths] = useState('3');
  const [discountPercent, setDiscountPercent] = useState('40');
  const [discountMonths, setDiscountMonths] = useState('1');
  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');

  const resetFromCoupon = (c: SignupCoupon | null) => {
    setCode(c?.code ?? '');
    setPlanCode(c?.plan_code ?? 'accelerate');
    setBenefitType(c?.benefit_type ?? 'free_months');
    setFreeMonths(String(c?.free_months ?? 3));
    setDiscountPercent(String(c?.discount_percent ?? 40));
    setDiscountMonths(String(c?.discount_months ?? 1));
    setMaxUses(c?.max_uses != null ? String(c.max_uses) : '');
    setExpiresAt(c?.expires_at ? c.expires_at.slice(0, 16) : '');
    setNotes(c?.notes ?? '');
  };

  useEffect(() => {
    if (open) resetFromCoupon(coupon);
  }, [open, coupon]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload: UpsertSignupCouponPayload = {
        code,
        plan_code: planCode,
        benefit_type: benefitType,
        free_months: benefitType === 'free_months' ? Number.parseInt(freeMonths, 10) : null,
        discount_percent: benefitType === 'discount' ? Number.parseInt(discountPercent, 10) : null,
        discount_months: benefitType === 'discount' ? Number.parseInt(discountMonths, 10) : null,
        max_uses: maxUses.trim() ? Number.parseInt(maxUses, 10) : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        notes: notes.trim() || null,
      };

      if (!payload.code.trim()) throw new Error('Code is required');
      if (benefitType === 'free_months' && (!payload.free_months || payload.free_months < 1)) {
        throw new Error('Free months must be at least 1');
      }
      if (benefitType === 'discount') {
        if (!payload.discount_percent || payload.discount_percent < 1 || payload.discount_percent > 100) {
          throw new Error('Discount percent must be 1–100');
        }
        if (!payload.discount_months || payload.discount_months < 1) {
          throw new Error('Discount months must be at least 1');
        }
      }

      if (isEdit && coupon) {
        return signupCouponsApi.update(coupon.id, payload);
      }
      return signupCouponsApi.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Coupon updated' : 'Coupon created');
      onSaved();
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={isEdit ? 'Edit signup coupon' : 'New signup coupon'}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs text-ink-muted mb-1">Code</label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="LAUNCH2026"
            disabled={isEdit}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-ink-muted mb-1">Plan</label>
            <select
              className="w-full bg-navy-card border border-navy-border rounded-lg px-3 py-2 text-sm text-white"
              value={planCode}
              onChange={(e) => setPlanCode(e.target.value as SignupPlanCode)}
            >
              {PLANS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-ink-muted mb-1">Benefit type</label>
            <select
              className="w-full bg-navy-card border border-navy-border rounded-lg px-3 py-2 text-sm text-white"
              value={benefitType}
              onChange={(e) => setBenefitType(e.target.value as SignupCouponBenefitType)}
            >
              {BENEFITS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {benefitType === 'free_months' ? (
          <div>
            <label className="block text-xs text-ink-muted mb-1">Free months</label>
            <Input value={freeMonths} onChange={(e) => setFreeMonths(e.target.value)} type="number" min={1} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-ink-muted mb-1">Discount %</label>
              <Input
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                type="number"
                min={1}
                max={100}
              />
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Discount months</label>
              <Input
                value={discountMonths}
                onChange={(e) => setDiscountMonths(e.target.value)}
                type="number"
                min={1}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-ink-muted mb-1">Max uses (blank = unlimited)</label>
            <Input value={maxUses} onChange={(e) => setMaxUses(e.target.value)} type="number" min={1} />
          </div>
          <div>
            <label className="block text-xs text-ink-muted mb-1">Expires at (optional)</label>
            <Input value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} type="datetime-local" />
          </div>
        </div>

        <div>
          <label className="block text-xs text-ink-muted mb-1">Notes (internal)</label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Partner invite, campaign…" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create coupon'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
